// ตอนนี้เด็กคนนี้อยู่ที่ไหน — ค้นชื่อแล้วเห็นสถานะตอนนี้ + เส้นเวลาของวันจากทุกโมดูล
//
// ใช้ตอนผู้ปกครองโทรมาถาม ตอนเด็กไม่อยู่ในหอก่อนนอน หรือตอนต้องตอบว่าเห็นเด็กครั้งสุดท้ายเมื่อไร
// "อยู่ในโรงเรียน" แปลว่าไม่มีบันทึกว่าออกไป ไม่ได้แปลว่ามีคนเห็นตัว — จึงแสดง "เห็นล่าสุด"
// คู่กันเสมอ (ดู server/models/whereaboutsModel.js)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Badge, Card, Col, Form, ListGroup, Row, Spinner } from 'react-bootstrap';
import { useSearchParams } from 'react-router-dom';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import ThaiDateInput from '../components/ThaiDateInput';
import { toThaiDateLong } from '../utils/thaiDate';

const API = process.env.REACT_APP_API || 'http://localhost:3002/api';
const UPLOAD_BASE = API.replace(/\/api\/?$/, '').replace(/\/$/, '');
const photoUrl = (photo) => (photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null);

const apiError = (error) => error?.response?.data?.message || 'ลองใหม่อีกครั้ง';

const timeOf = (value) => {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5);
};

const dateTimeOf = (value) => {
  if (!value) return '';
  const d = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '' : `${toThaiDateLong(d, { short: true })} ${d.toTimeString().slice(0, 5)} น.`;
};

const Avatar = ({ photo, name, size = 44 }) => {
  const src = photoUrl(photo);
  return src ? (
    <img src={src} alt={name} width={size} height={size} className="rounded-circle flex-shrink-0" style={{ objectFit: 'cover' }} />
  ) : (
    <span className="rounded-circle bg-secondary bg-opacity-25 d-inline-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: size, height: size }}>
      <i className="ri-user-line text-secondary"></i>
    </span>
  );
};

const dormText = (s) => (s.village_name
  ? `${s.village_name}${s.village_gender ? (s.village_gender === 'm' ? ' (ชาย)' : ' (หญิง)') : ''} · หลัง ${s.dorm_name}`
  : 'ไม่มีทะเบียนหอ');

const StudentDay = ({ stId, date }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setData(null);
    setError('');
    axiosInstance.get(`${API}/safety/whereabouts/students/${stId}`, { params: { date: date || undefined } })
      .then(({ data: res }) => { if (alive) setData(res); })
      .catch((e) => { if (alive) setError(apiError(e)); });
    return () => { alive = false; };
  }, [stId, date]);

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!data) return <div className="text-center py-5"><Spinner animation="border" /></div>;

  const { student, now, lastSeen, events, leaves, prayers } = data;

  return (
    <>
      <Card className="card-one mb-3">
        <Card.Body className="d-flex gap-3 align-items-center">
          <Avatar photo={student.st_photo} name={student.st_name} size={64} />
          <div className="flex-fill min-width-0">
            <div className="fs-5 fw-semibold text-truncate">{student.st_name}</div>
            <div className="fs-sm text-secondary">
              {student.st_idnumber}{student.class_label ? ` · ${student.class_label}` : ''}
            </div>
            <div className="fs-sm text-secondary">{dormText(student)}</div>
            {(student.st_parentPhone || student.st_emergencyPhone) && (
              <div className="fs-sm mt-1">
                <i className="ri-phone-line me-1"></i>
                {[student.st_parentPhone, student.st_emergencyPhone].filter(Boolean).map((p) => (
                  <a key={p} href={`tel:${p}`} className="me-2">{p}</a>
                ))}
              </div>
            )}
          </div>
        </Card.Body>
      </Card>

      {now && (
        <Alert variant={now.tone === 'secondary' ? 'light' : now.tone} className="mb-3">
          <div className="fw-semibold"><i className="ri-map-pin-user-line me-1"></i>ตอนนี้: {now.text}</div>
          {now.detail && <div className="fs-sm">{now.detail}</div>}
          {now.since && <div className="fs-sm">ตั้งแต่ {dateTimeOf(now.since)}</div>}
          <div className="fs-sm mt-1">
            เห็นตัวล่าสุด: {lastSeen ? `${timeOf(lastSeen.at)} น. — ${lastSeen.title}` : 'ไม่มีบันทึกที่ยืนยันว่าเห็นตัวในวันนี้'}
          </div>
        </Alert>
      )}

      {leaves.length > 0 && (
        <Card className="card-one mb-3">
          <Card.Header><Card.Title as="h6" className="mb-0">ใบอนุญาตที่เกี่ยวข้อง</Card.Title></Card.Header>
          <ListGroup variant="flush">
            {leaves.map((l, i) => (
              <ListGroup.Item key={i} className="fs-sm">
                <div className="fw-semibold">{l.title}</div>
                {l.detail && <div className="text-secondary">{l.detail}</div>}
                <div>
                  ออกประตู: {l.out_at ? dateTimeOf(l.out_at) : <span className="text-secondary">ยังไม่มีบันทึก</span>}
                  {' · '}กลับเข้า: {l.in_at ? dateTimeOf(l.in_at) : <span className="text-danger">ยังไม่มีบันทึก</span>}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card>
      )}

      {prayers.length > 0 && (
        <Card className="card-one mb-3">
          <Card.Body className="d-flex flex-wrap gap-2">
            {prayers.map((p) => (
              <div key={p.key} className="text-center" style={{ minWidth: 64 }}>
                <div className="fs-xs text-secondary">{p.label}</div>
                <Badge bg={p.tone} text={p.tone === 'light' || p.tone === 'warning' ? 'dark' : undefined}>{p.text}</Badge>
                {p.time && <div className="fs-xs text-secondary">{p.time}</div>}
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      <Card className="card-one">
        <Card.Header><Card.Title as="h6" className="mb-0">เส้นเวลา {toThaiDateLong(data.date)}</Card.Title></Card.Header>
        {events.length === 0 ? (
          <Card.Body className="text-secondary text-center">ไม่มีบันทึกของวันนี้</Card.Body>
        ) : (
          <ListGroup variant="flush">
            {events.map((e, i) => (
              <ListGroup.Item key={i} className="d-flex gap-3 fs-sm">
                <div className="text-secondary" style={{ width: 44 }}>{timeOf(e.at)}</div>
                <div className="flex-fill">
                  <Badge bg={e.tone} text={e.tone === 'warning' ? 'dark' : undefined} className="me-2">&nbsp;</Badge>
                  {e.title}
                  {e.detail && <div className="text-secondary">{e.detail}</div>}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card>
    </>
  );
};

export default function Whereabouts() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const [params, setParams] = useSearchParams();
  const stId = Number(params.get('st')) || null;
  const date = params.get('date') || '';
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);

  const search = useCallback(async (q) => {
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await axiosInstance.get(`${API}/safety/whereabouts/search`, { params: { q } });
      setResults(data);
      setError('');
    } catch (e) {
      setError(apiError(e));
    } finally {
      setSearching(false);
    }
  }, []);

  const onType = (value) => {
    setKeyword(value);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(value), 300);
  };

  const pick = (id) => {
    setResults([]);
    setKeyword('');
    setParams({ st: String(id), ...(date ? { date } : {}) });
  };

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="mb-3">
          <h4 className="main-title mb-0">ตอนนี้อยู่ที่ไหน</h4>
          <div className="fs-xs text-secondary">รวมบันทึกประตู ห้องพยาบาล ละหมาด คาบเรียน และเช็คชื่อหอ ของนักเรียนหนึ่งคน</div>
        </div>

        <Row className="g-2 mb-3">
          <Col xs={12} md={8} className="position-relative">
            <Form.Control
              value={keyword}
              onChange={(e) => onType(e.target.value)}
              placeholder="พิมพ์ชื่อหรือเลขประจำตัว อย่างน้อย 2 ตัวอักษร"
              autoFocus
            />
            {searching && <Spinner size="sm" animation="border" className="position-absolute" style={{ right: 16, top: 10 }} />}
            {results.length > 0 && (
              <ListGroup className="position-absolute w-100 shadow" style={{ zIndex: 20, maxHeight: 360, overflowY: 'auto' }}>
                {results.map((s) => (
                  <ListGroup.Item key={s.st_id} action onClick={() => pick(s.st_id)} className="d-flex gap-2 align-items-center">
                    <Avatar photo={s.st_photo} name={s.st_name} size={36} />
                    <div className="min-width-0">
                      <div className="text-truncate">{s.st_name}</div>
                      <div className="fs-xs text-secondary">{s.st_idnumber} · {dormText(s)}</div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </Col>
          <Col xs={12} md={4}>
            <ThaiDateInput
              value={date}
              onChange={(v) => setParams({ ...(stId ? { st: String(stId) } : {}), ...(v ? { date: v } : {}) })}
              placeholder="วันนี้"
            />
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        {stId
          ? <StudentDay stId={stId} date={date} />
          : (
            <div className="text-center text-secondary py-5">
              <i className="ri-search-eye-line d-block mb-2" style={{ fontSize: 40 }}></i>
              ค้นชื่อนักเรียนเพื่อดูว่าตอนนี้อยู่ที่ไหน
            </div>
          )}

        <Footer />
      </div>
    </>
  );
}
